import { Dropdown, Menu } from 'antd';
import { DrawerId, useDrawer } from 'common/components';
import { APP_VERSION } from 'common/constants';
import { AddIcon, BalanceAILightIcon } from 'common/icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  FC,
  Key,
  MouseEvent,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from 'src/auth';
import EntitlementEnum from 'src/pages/organization/entitlements.enum';

import { useQuickActionList } from '../quick-action-list.hooks';
import { MenuItemModel } from './menu-item/menu-item.model';
import { MenuTrigger } from './menu-trigger/menu-trigger.component';
import { useSiderNavbar } from './sider-navbar.hook';
import * as Styled from './sider-navbar.style';

function getItem(
  label: ReactNode,
  key: Key,
  icon?: ReactNode,
  children?: MenuItemModel[],
  onClick?: (e: MouseEvent<HTMLElement>) => void,
): MenuItemModel {
  return {
    key,
    icon,
    label,
    children,
    onClick,
  } as MenuItemModel;
}

const SiderNavbarComponent: FC = () => {
  const [searchParams] = useSearchParams();

  const isBalanceAiOpen = searchParams.get('balanceAi[open]') === 'true';
  const { user } = useAuth();
  const { quickActions } = useQuickActionList();
  const { balanceAiFms1865QuickActions } = useFlags();
  const { handleFeatureItems, current, entitlements } = useSiderNavbar({
    isMobile: false,
  });
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const { t } = useTranslation();
  const featureFlags = useFlags();
  const { open: openDrawer } = useDrawer();

  const dropdownOffset: [number, number] = collapsed ? [60, 40] : [270, 40];
  const quickActionsClassName: string = collapsed ? '' : 'full-width';

  useEffect(() => {
    if (isBalanceAiOpen) {
      openDrawer(DrawerId.BalanceAi);
    }
  }, [isBalanceAiOpen, openDrawer]);

  const handleQuickActionsButton = useMemo(() => {
    if (!user) return null;

    if (
      balanceAiFms1865QuickActions &&
      entitlements.includes(EntitlementEnum.BALANCE_AI_VOICE_ACTIONS)
    ) {
      return (
        <Styled.QuickActionsButtonAI
          type="default"
          shape={'default'}
          size="large"
          icon={<BalanceAILightIcon />}
          className={quickActionsClassName}
          data-testid="balance-ai-quick-actions-button"
          onClick={() => openDrawer(DrawerId.BalanceAi)}
          id="balance-ai-quick-actions-button"
        />
      );
    } else {
      return (
        <Dropdown
          menu={{
            items: quickActions
              .filter(
                (value) =>
                  !featureFlags[value.feature_flag_disable ?? ''] &&
                  (value.entitlement === undefined ||
                    entitlements.includes(value.entitlement)),
              )
              .map((action, index) =>
                getItem(
                  action.description,
                  index,
                  action.icon,
                  undefined,
                  action.onClick,
                ),
              ),
          }}
          align={{
            offset: dropdownOffset,
            useCssBottom: true,
          }}
          placement="topRight"
          trigger={['click']}
        >
          <Styled.QuickActionsButton
            type="default"
            shape={'default'}
            size="large"
            icon={<AddIcon />}
            className={quickActionsClassName}
          >
            {!collapsed && t('sider.quick_actions')}
          </Styled.QuickActionsButton>
        </Dropdown>
      );
    }
  }, [
    user,
    balanceAiFms1865QuickActions,
    quickActions,
    entitlements,
    featureFlags,
    collapsed,
    t,
    openDrawer,
  ]);

  return (
    <Styled.Sider
      theme="light"
      collapsible
      collapsed={collapsed}
      onCollapse={(value) => setCollapsed(value)}
      collapsedWidth={64}
      width={280}
      trigger={<MenuTrigger collapsed={collapsed} />}
    >
      {handleQuickActionsButton}
      <Menu
        selectedKeys={[current as string]}
        mode="inline"
        items={handleFeatureItems()}
      />
      {APP_VERSION && !collapsed && (
        <Styled.Version>v{APP_VERSION}</Styled.Version>
      )}
    </Styled.Sider>
  );
};

export { SiderNavbarComponent };
